import { GiftIcon } from "@heroicons/react/24/outline";
import React from "react";
import { ModalWrapper } from ".";
import { PrimaryButton } from "../Buttons";
import { formatCurrency } from "../../utils/helpers";

// REF: SPR-003 T-002 — cancel-flow retention. Shown when a subscriber initiates
// cancel and Margot's PreviewCancellationOffers returns an eligible Retention offer.
// Accept keeps the subscription (discount applies to the next renewal); Decline
// falls through to the normal cancel confirmation.
export default function RetentionOfferModal({
  modalOpen,
  setModalOpen,
  offer,
  onAccept,
  onDecline,
}) {
  if (!offer) return null;

  const isPercentage = offer.discountType === "Percentage" && !!offer.discountPercent;
  const discountLabel = isPercentage
    ? `${offer.discountPercent}% off`
    : `${formatCurrency(offer.price, true)}`;

  // REF: SPR-004 T-002 — HANDOFF-004 §2: when discountMode is "RecurringPerPeriod",
  // Margot ignores durationCount/durationType entirely and instead repeats the
  // discount for `recurringPeriods` consecutive billing periods at the product's own
  // term. Showing the old duration-based copy for these offers would be misleading,
  // so branch on discountMode. SingleTerm / unset / legacy offers (no discountMode)
  // keep today's exact duration-based copy — no change for those.
  const isRecurringOffer =
    offer.discountMode === "RecurringPerPeriod" && !!offer.recurringPeriods;
  const durationLabel = isRecurringOffer
    ? `repeats for the next ${offer.recurringPeriods} billing periods`
    : offer.durationCount
    ? `for ${offer.durationCount} ${String(offer.durationType || "").toLowerCase()}`
    : "";

  return (
    <ModalWrapper open={modalOpen} setOpen={setModalOpen}>
      <div className="w-[85vw] lg:w-[35vw]">
        <div className="pb-4 text-center flex items-center flex-col">
          <GiftIcon className="w-8 text-indigo-500" />
          <span className="mt-2 font-semibold">Before you go — a special offer</span>
        </div>

        <div className="pb-4 mx-6 text-center">
          {/* REF: SPR-004 T-002 — PreviewCancellationOffers is now best-value ordered
              server-side and this component is always handed offers[0], so the offer
              shown here IS the best one; label it as such (presentation-only, no
              selection-logic change). */}
          <span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-700 mb-1">
            Best value
          </span>
          <div className="text-lg font-semibold text-slate-900">{offer.name}</div>
          <div className="mt-1 text-2xl font-bold text-indigo-600">{discountLabel}</div>
          {durationLabel && (
            <div className="text-sm text-gray-500">{durationLabel}</div>
          )}
        </div>

        <div className="pb-8 mx-6 text-center text-sm text-gray-500">
          Keep your subscription and this discount applies to your next renewal.
        </div>

        <div className="mt-3">
          <PrimaryButton
            text={"Keep my subscription"}
            onClick={onAccept}
          />
        </div>

        <div className="mt-3 text-center">
          <button
            type="button"
            className="text-sm text-gray-500 underline hover:opacity-70"
            onClick={onDecline}
          >
            No thanks, cancel anyway
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
}
